import { format, startOfMonth, subMonths } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

import { useGetPaymentHealth } from '~entities/dashboard/api/get-payment-health';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '~shared/shadcn/ui/chart';

const chartConfig = {
  success: {
    label: 'Success Rate',
    color: '#10b981',
  },
  failure: {
    label: 'Failure Rate',
    color: '#ef4444',
  },
  refunded: {
    label: 'Refunded Amount',
    color: '#f59e0b',
  },
};

const today = new Date();
const formattedToday = format(today, 'yyyy-MM-dd');
const sixMonthsAgo = subMonths(today, 5);
const firstOfMonth = startOfMonth(sixMonthsAgo);
const formattedFirstOfMonth = format(firstOfMonth, 'yyyy-MM-dd');

interface PaymentHealthChartProps {
  sessionId: string;
}

export function PaymentHealthChart({ sessionId }: PaymentHealthChartProps) {
  const { data: paymentHealthData, isLoading: isPaymentHealthLoading } =
    useGetPaymentHealth({
      sessionId,
      startDate: formattedFirstOfMonth,
      endDate: formattedToday,
    });

  return (
    <Card className='min-w-0 overflow-hidden'>
      <CardHeader>
        <CardTitle>Payment Health</CardTitle>
        <CardDescription>
          Payment success vs failure rate (Last 6 months)
        </CardDescription>
      </CardHeader>
      <CardContent className='min-w-0 overflow-hidden'>
        {isPaymentHealthLoading ? (
          <div className='flex h-[300px] items-center justify-center'>
            <Loader2 className='size-10 animate-spin' />
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className='aspect-auto h-[280px] w-full min-w-0 max-w-full overflow-hidden sm:h-[300px]'
          >
            <LineChart
              data={paymentHealthData}
              margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray='3 3' vertical={false} />
              <XAxis
                dataKey='month'
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
                interval='preserveStartEnd'
              />
              <YAxis
                width={36}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line
                type='monotone'
                dataKey='success'
                stroke='#10b981'
                strokeWidth={2}
                dot={false}
              />
              <Line
                type='monotone'
                dataKey='failure'
                stroke='#ef4444'
                strokeWidth={2}
                dot={false}
              />
              <Line
                type='monotone'
                dataKey='refunded'
                stroke='#f59e0b'
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
